<template>
    <div class="date-picker">
        <label v-if="props.label" class="date-picker__label" :for="name">{{
            props.label
        }}</label>

        <div class="input-group">
            <input
                :class="[
                    'input',
                    {
                        'input--inline': !isDisplayBlock,
                        'input--invalid': props.hasError,
                        'has-bg': props.bg,
                    },
                ]"
                ref="flatpickrDOM"
                v-bind="$attrs"
                :name="props.name"
                :disabled="props.disabled"
                :placeholder="props.placeholder"
                :value="modelValue"
                @input="inputHandler"
            />
            <ErrorMessage
                v-if="props.hasError && props.name"
                :name="props.name"
                class="error-message"
                as="div"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import Flatpickr from 'flatpickr';
import {Korean} from 'flatpickr/dist/l10n/ko';
import {Instance} from 'modules/flatpickr/dist/types/instance';
import {ErrorMessage} from 'vee-validate';
import {onMounted, onUnmounted, ref, toRef, watch} from 'vue';

// props, context, emits, libs
const props = defineProps({
    modelValue: {
        type: String, // NOTE: '2023-04-11' 또는 '2023-04-11 13:00' 형식
        default: '',
    },
    label: {
        type: String,
        default: '',
    },
    name: {
        type: String,
        required: true,
    },
    placeholder: {
        type: String,
        default: '',
    },
    minDate: {
        type: String,
        default: '1900-01',
    },
    maxDate: {
        type: String,
    },
    enableTime: {
        // Time Range 사용 여부
        // See: https://flatpickr.js.org/examples/#datetimepicker-with-limited-time-range
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    isDisplayBlock: {
        type: Boolean,
        default: false,
    },
    hasError: {
        type: Boolean,
        default: false,
    },
    minuteIncrement: {
        type: Number,
    },
    bg: {
        type: Boolean,
        default: false,
    },
});

const emits = defineEmits<{
    (name: 'update:modelValue', value: typeof props.modelValue): void;
    (name: 'input', event: Event): void;
    (name: 'blur', event: Event): void;
    (
        name: 'updateDate',
        dateInfo: {
            selectedDates: Date[];
            dateString: string;
            instance: Instance;
        },
    ): void;
    (name: 'destroyDatePicker'): void;
}>();

// ref, reactive
const name = toRef(props, 'name');
const flatpickrDOM = ref<HTMLElement | null>(null);
const flatpickr = ref<Instance | null>(null);

// computed
// methods
const inputHandler = (e: Event) => {
    const target = e.target as HTMLInputElement;

    emits('update:modelValue', target.value);
    emits('input', e);
};

const destroyDatePicker = () => {
    flatpickr.value?.destroy();
    emits('destroyDatePicker');
};

// effect (created, mounted, watch, VueUse)
watch(
    () => props.maxDate,
    () => {
        if (props.maxDate && props.maxDate < props.modelValue) {
            // NOTE: 입력받은 날짜가 업데이트된 maxDate보다 더 미래의 날짜일때, 입력받은 날짜(input 값)을 초기화
            emits('update:modelValue', '');
        }

        // NOTE: maxDate가 실시간으로 변동되면, 새로운 maxDate 값 세팅을 위해 flatpickrDOM 재초기화
        if (flatpickrDOM.value) {
            flatpickr.value = Flatpickr(flatpickrDOM.value, {
                locale: Korean,
                altInputClass: 'single-calendar',
                disableMobile: true,
                ...(props.enableTime && {enableTime: props.enableTime}),
                ...(props.minDate && {minDate: props.minDate}),
                ...(props.maxDate && {maxDate: props.maxDate}),
                ...(props.minuteIncrement && {
                    minuteIncrement: props.minuteIncrement,
                }),
                onChange: (selectedDates, dateString, instance) =>
                    emits('updateDate', {selectedDates, dateString, instance}),
            });
        }
    },
);

onMounted(() => {
    if (flatpickrDOM.value) {
        flatpickr.value = Flatpickr(flatpickrDOM.value, {
            locale: Korean,
            altInputClass: 'single-calendar',
            disableMobile: true,
            ...(props.enableTime && {enableTime: props.enableTime}),
            ...(props.minDate && {minDate: props.minDate}),
            ...(props.maxDate && {maxDate: props.maxDate}),
            ...(props.minuteIncrement && {
                minuteIncrement: props.minuteIncrement,
            }),
            onChange: (selectedDates, dateString, instance) =>
                emits('updateDate', {selectedDates, dateString, instance}),
        });
    }
});

onUnmounted(() => {
    destroyDatePicker();
});
</script>

<style lang="scss" scoped>
.date-picker {
    @include flex;

    &__label {
        width: 83px;
        margin: 7px 16px 0 0;
    }

    .input-group {
        flex: 1 auto;

        .input {
            position: relative;
            display: block;
            width: 100%;
            height: 38px;
            padding: 0 39px 0 12px;
            border: 1px solid $black_16;
            border-radius: 6px;
            background: url('data:image/svg+xml;utf-8, <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill="rgba(0, 0, 0, 0.32)" d="M13 3.5V4.4H7V3.5C7 2.94772 6.55228 2.5 6 2.5C5.44772 2.5 5 2.94772 5 3.5V4.57071C3.83481 4.98254 3 6.09378 3 7.4V14.5C3 16.1569 4.34315 17.5 6 17.5H14C15.6569 17.5 17 16.1569 17 14.5V7.4C17 6.09378 16.1652 4.98254 15 4.57071V3.5C15 2.94772 14.5523 2.5 14 2.5C13.4477 2.5 13 2.94772 13 3.5ZM5 13.6V9H15V13.6C15 14.7046 14.1046 15.6 13 15.6H7C5.89543 15.6 5 14.7046 5 13.6Z" /></svg>')
                no-repeat right 10px center; // @/assets/icon/icn_calendar.svg
            cursor: pointer;

            &--inline {
                display: inline-block;
                width: auto;
            }

            &--invalid {
                border-color: $danger;
            }

            &:hover {
                border: 1px solid $black_40;
            }

            &:focus {
                border: 1px solid $primary;
            }

            &:disabled {
                color: $black_16;
                background-color: $black_08;
                cursor: not-allowed;
            }

            &::placeholder {
                color: $black_32;
            }

            &.has-bg {
                background-color: $bg-field;
                max-width: 300px;
                border: none;
            }
        }
    }
}
</style>
